/**
 * Shared trade store -- localStorage-backed with Supabase sync when authenticated.
 */

import { getCurrentUserId, dbInsert, dbDelete, dbSelect, dbUpdate } from "./dbSync";

export interface StoredTrade {
  id: string;
  date: string;
  time: string;
  instrument: string;
  direction: "long" | "short";
  entryPrice: number;
  exitPrice: number;
  stopLoss: number;
  takeProfit: number;
  rr: number;
  pnl: number;
  pnlPercent: number;
  lotSize: number;
  strategy: string;
  session: string;
  emotion: string;
  notes: string;
  hasScreenshot: boolean;
  propAccountId?: string | null;
  tags: string[];
}

const STORAGE_KEY = "mm-trade-logs";
const listeners = new Set<() => void>();
let cachedRaw: string | null = null;
let cachedSnapshot: StoredTrade[] = [];

function readLocal(): StoredTrade[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw !== cachedRaw) {
      cachedRaw = raw;
      cachedSnapshot = raw ? JSON.parse(raw) : [];
    }
    return cachedSnapshot;
  } catch {
    return [];
  }
}

function writeLocal(trades: StoredTrade[]) {
  const json = JSON.stringify(trades);
  cachedRaw = json;
  cachedSnapshot = trades;
  localStorage.setItem(STORAGE_KEY, json);
  listeners.forEach((fn) => fn());
}

function notify() {
  listeners.forEach((fn) => fn());
}

/** Load trades from DB and replace local cache */
export async function syncTradesFromDB() {
  if (!getCurrentUserId()) return;
  const rows = await dbSelect<any>("trades", "created_at");
  if (rows.length > 0) {
    const trades: StoredTrade[] = rows.map((r: any) => ({
      id: r.id,
      date: r.date,
      time: r.time,
      instrument: r.instrument,
      direction: r.direction as "long" | "short",
      entryPrice: Number(r.entry_price),
      exitPrice: Number(r.exit_price),
      stopLoss: Number(r.stop_loss),
      takeProfit: Number(r.take_profit),
      rr: Number(r.rr),
      pnl: Number(r.pnl),
      pnlPercent: Number(r.pnl_percent),
      lotSize: Number(r.lot_size),
      strategy: r.strategy,
      session: r.session,
      emotion: r.emotion,
      notes: r.notes,
      hasScreenshot: r.has_screenshot,
      propAccountId: r.prop_account_id || null,
      tags: r.tags || [],
    }));
    cachedSnapshot = trades;
    cachedRaw = JSON.stringify(trades);
    localStorage.setItem(STORAGE_KEY, cachedRaw);
    notify();
  } else {
    // No trades in DB -- clear any leftover local data so new users start fresh
    cachedSnapshot = [];
    cachedRaw = "[]";
    localStorage.setItem(STORAGE_KEY, "[]");
    notify();
  }
}

function tradeToRow(t: StoredTrade) {
  return {
    date: t.date,
    time: t.time,
    instrument: t.instrument,
    direction: t.direction,
    entry_price: t.entryPrice,
    exit_price: t.exitPrice,
    stop_loss: t.stopLoss,
    take_profit: t.takeProfit,
    rr: t.rr,
    pnl: t.pnl,
    pnl_percent: t.pnlPercent,
    lot_size: t.lotSize,
    strategy: t.strategy,
    session: t.session,
    emotion: t.emotion,
    notes: t.notes,
    has_screenshot: t.hasScreenshot,
    prop_account_id: t.propAccountId || null,
    tags: t.tags || [],
  };
}

export function getTrades(): StoredTrade[] {
  return readLocal();
}

export function getOpenTrades(): StoredTrade[] {
  return readLocal().filter(t => t.exitPrice == null || t.exitPrice === t.entryPrice);
}

export async function addTrade(trade: Omit<StoredTrade, "id">): Promise<StoredTrade> {
  // If authenticated, insert to DB first to get the real UUID
  if (getCurrentUserId()) {
    const row = await dbInsert("trades", tradeToRow(trade as StoredTrade));
    if (row) {
      const entry: StoredTrade = {
        ...trade,
        id: row.id,
        entryPrice: Number(row.entry_price),
        exitPrice: Number(row.exit_price),
        stopLoss: Number(row.stop_loss),
        takeProfit: Number(row.take_profit),
        rr: Number(row.rr),
        pnl: Number(row.pnl),
        pnlPercent: Number(row.pnl_percent),
        lotSize: Number(row.lot_size),
      };
      writeLocal([entry, ...readLocal()]);
      return entry;
    }
  }
  // Fallback: local only
  const entry: StoredTrade = { ...trade, id: crypto.randomUUID() };
  writeLocal([entry, ...readLocal()]);
  return entry;
}

export async function updateTrade(id: string, updates: Partial<Omit<StoredTrade, "id">>) {
  const trades = readLocal();
  const idx = trades.findIndex((t) => t.id === id);
  if (idx === -1) return;
  const updated = { ...trades[idx], ...updates };
  trades[idx] = updated;
  writeLocal(trades);
  if (getCurrentUserId()) {
    await dbUpdate("trades", { id }, tradeToRow(updated));
  }
}

export async function deleteTrade(id: string) {
  writeLocal(readLocal().filter((t) => t.id !== id));
  if (getCurrentUserId()) {
    await dbDelete("trades", id);
  }
}

export async function clearAllTrades() {
  writeLocal([]);
  if (getCurrentUserId()) {
    const { supabase } = await import("@/integrations/supabase/client");
    await (supabase as any).from("trades").delete().eq("user_id", getCurrentUserId());
  }
}

export async function snapshotTrades(name: string): Promise<void> {
  const userId = getCurrentUserId();
  if (!userId) return;
  const { supabase } = await import("@/integrations/supabase/client");

  const trades = readLocal();
  const tradeCount = trades.length;
  if (tradeCount === 0) return;

  const totalPnl = trades.reduce((s, t) => s + (t.pnl || 0), 0);
  const wins = trades.filter(t => (t.pnl || 0) > 0).length;
  const winRate = (wins / tradeCount) * 100;

  const tradesWithRr = trades.filter(t => t.rr && t.rr !== 0);
  const avgRr = tradesWithRr.length > 0
    ? tradesWithRr.reduce((s, t) => s + t.rr, 0) / tradesWithRr.length
    : 0;

  const dates = trades.map(t => t.date).filter(Boolean).sort();
  const periodStart = dates[0] ?? null;
  const periodEnd = dates[dates.length - 1] ?? null;

  const dailyMap: Record<string, number> = {};
  for (const t of trades) {
    if (!t.date) continue;
    dailyMap[t.date] = (dailyMap[t.date] || 0) + (t.pnl || 0);
  }
  const dailyPnls = Object.values(dailyMap);
  const bestDayPnl = dailyPnls.length > 0 ? Math.max(...dailyPnls) : 0;
  const worstDayPnl = dailyPnls.length > 0 ? Math.min(...dailyPnls) : 0;
  const avgDailyPnl = dailyPnls.length > 0
    ? dailyPnls.reduce((s, v) => s + v, 0) / dailyPnls.length
    : 0;

  await (supabase as any).from("account_snapshots").insert({
    user_id: userId,
    name,
    period_start: periodStart,
    period_end: periodEnd,
    trade_count: tradeCount,
    total_pnl: totalPnl,
    win_rate: winRate,
    avg_rr: avgRr,
    best_day_pnl: bestDayPnl,
    worst_day_pnl: worstDayPnl,
    avg_daily_pnl: avgDailyPnl,
  });
}

export function subscribeTrades(fn: () => void) {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}
